import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SourcingEventStatus, useGetSourcingEventQuery } from "../../../autogen/bff-api";
import { useSub } from "../../../common/subscription/useSub";
import { urls } from "../../../urls";
import { EventHeader } from "./EventHeader";
import { EventPhaseTimeline } from "./timeline/EventPhaseTimeline";
import { EventTabs, parseEventTab, type EventTab } from "./EventTabs";

export const SourcingEventView = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sub = useSub();

  const { data: event, isLoading, isError } = useGetSourcingEventQuery(
    sub.hasSourcingEventV2Access && eventId ? { eventId } : skipToken,
  );

  if (!sub.hasSourcingEventV2Access) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (isError || !event) {
    return (
      <Center py="20">
        <Text color="smError">{t("Could not load this sourcing event.")}</Text>
      </Center>
    );
  }

  const activeTab = parseEventTab(searchParams.get("tab"));
  const onTabChange = (tab: EventTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const canEdit = event.status === SourcingEventStatus.Draft && sub.canCreateEvents;
  const onEdit = () => {
    const paths = urls.publicEvents.edit.go(event.id);
    navigate(`${paths.info.pathname}?${paths.info.search}`);
  };
  const onViewPublic = () => navigate(urls.portal.view.go(event.id));

  return (
    <Box maxW="container.xl" mx="auto">
      <EventHeader event={event} canEdit={canEdit} onEdit={onEdit} onViewPublic={onViewPublic} />
      <EventPhaseTimeline event={event} />
      <Box mt="6">
        <EventTabs event={event} activeTab={activeTab} onTabChange={onTabChange} />
      </Box>
    </Box>
  );
};
